/**
 * fix-mojibake.js
 *
 * INTERIM front-end patch for CSV-import encoding issues (e.g.
 * "puppyâ€™s" -> "puppy's", "Â " -> non-breaking space).
 *
 * This does NOT fix the underlying product data in Shopify -- it only
 * repairs how the text LOOKS to visitors, by scanning the rendered page
 * after load and reversing the double-encoding. Once the real product
 * data has been cleaned up (batch by batch), remove this script.
 *
 * WHAT IT FIXES: visible text on the page (product descriptions, titles,
 * etc.) and, optionally, the <title> tag and meta description/og tags,
 * since those are also read from the DOM by most modern crawlers.
 *
 * WHAT IT DOES NOT FIX: raw HTML served to non-JS clients (rare bots,
 * some scrapers), RSS/product feeds, emails, or anything read directly
 * from Shopify's admin/API rather than the rendered page.
 *
 * HOW IT WORKS (safely):
 * The classic mojibake here happens when UTF-8 bytes are misread as
 * Windows-1252. This script reverses that: it takes the garbled text,
 * maps each character back to the Windows-1252 byte it would have come
 * from, then decodes those bytes as UTF-8. If that round-trip doesn't
 * produce a clean, valid result, the original text is left completely
 * untouched -- so this cannot corrupt text that wasn't actually mojibake
 * to begin with (e.g. genuine accented text like "café" is left alone).
 *
 * INSTALL (Shopify Admin):
 *   1. Online Store > Themes > Edit code
 *   2. Assets > Add a new asset > Upload this file (fix-mojibake.js)
 *   3. Open layout/theme.liquid, and just before </body> add:
 *        <script src="{{ 'fix-mojibake.js' | asset_url }}" defer></script>
 *   4. Save. It will now run on every page. To scope it to product pages
 *      only, wrap that line in {% if template contains 'product' %} ... {% endif %}
 */
(function () {
  "use strict";

  // ---- Config ----------------------------------------------------------
  // Only fix text inside these containers (keeps it fast and avoids ever
  // touching nav/footer/app-embed markup you don't control). Add more
  // selectors if other areas show the issue.
  var TARGET_SELECTORS = [
    ".product__description",
    ".product-single__description",
    "[data-product-description]",
    "main",
  ];
  // Also fix <title> and meta description/og tags, since crawlers and
  // browser tabs read those too.
  var FIX_META_AND_TITLE = true;

  // ---- Windows-1252 reverse map (codepoint -> byte 0x80-0x9F) ----------
  var WIN1252_REVERSE = {
    0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85,
    0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a,
    0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92,
    0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
    0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c,
    0x017e: 0x9e, 0x0178: 0x9f
  };

  // Quick pre-check so we don't do byte-level work on text that has no
  // chance of being this kind of mojibake.
  var MOJIBAKE_HINT = /[ÂÃâ€]/;

  var decoder;
  try {
    decoder = new TextDecoder("utf-8", { fatal: true });
  } catch (e) {
    decoder = null; // very old browser; script will just no-op safely
  }

  function fixMojibake(text) {
    if (!decoder || !text || !MOJIBAKE_HINT.test(text)) return text;

    var bytes = [];
    for (var i = 0; i < text.length; i++) {
      var cp = text.codePointAt(i);
      // handle surrogate pairs (astral codepoints can't be single-byte
      // Windows-1252 anyway, so bail out immediately -- not this kind of issue)
      if (cp > 0xffff) return text;
      var byte;
      if (cp < 0x80) {
        byte = cp;
      } else if (WIN1252_REVERSE[cp] !== undefined) {
        byte = WIN1252_REVERSE[cp];
      } else if (cp <= 0xff) {
        byte = cp;
      } else {
        return text; // not representable as a single Windows-1252 byte -> leave alone
      }
      bytes.push(byte);
    }

    try {
      var decoded = decoder.decode(new Uint8Array(bytes));
      return decoded; // fatal:true means this line only runs if it's genuinely valid UTF-8
    } catch (e) {
      return text; // invalid re-decode -> wasn't actually this kind of mojibake, leave untouched
    }
  }

  function walkAndFix(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        var tag = node.parentNode && node.parentNode.nodeName;
        if (tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT") {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    var node;
    var fixedCount = 0;
    while ((node = walker.nextNode())) {
      var fixed = fixMojibake(node.nodeValue);
      if (fixed !== node.nodeValue) {
        node.nodeValue = fixed;
        fixedCount++;
      }
    }
    return fixedCount;
  }

  function fixMetaAndTitle() {
    if (document.title) {
      var fixedTitle = fixMojibake(document.title);
      if (fixedTitle !== document.title) document.title = fixedTitle;
    }
    var metaSelectors = [
      'meta[name="description"]',
      'meta[property^="og:"]',
      'meta[name^="twitter:"]'
    ];
    metaSelectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        var content = el.getAttribute("content");
        if (!content) return;
        var fixed = fixMojibake(content);
        if (fixed !== content) el.setAttribute("content", fixed);
      });
    });
  }

  function run() {
    var totalFixed = 0;
    var seen = new Set();
    TARGET_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (root) {
        if (seen.has(root)) return; // avoid double-processing overlapping selectors (e.g. "main" containing ".product__description")
        seen.add(root);
        totalFixed += walkAndFix(root);
      });
    });
    if (FIX_META_AND_TITLE) fixMetaAndTitle();

    if (window.location.search.indexOf("mojibake_debug=1") !== -1) {
      console.log("[fix-mojibake] text nodes fixed:", totalFixed);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})();
